import React, { useMemo } from 'react';
import { Column } from '@shared/ui/atoms/StructuralLayout';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { useDispatch, useSelector } from "react-redux";
import { selectActiveOffices, selectAllOfficeEntities } from "@features/desks/store/advanceHotDeskingSetup.duck";
import styled from "styled-components";
import { HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { selectActiveDay } from "../../../../store/editMovements.duck";
import { GenericDropDownList } from '@shared/ui/molecules/GenericDropDownList';
import { clearSelectedArea, selectedRootOffice, selectOffice } from "../../../../store/officeView.duck";
import { findOfficeInOffices, getOfficeChildren, } from "@shared/utils/OfficeHelper";
import isMobile from "is-mobile";
import { WeeklyOfficeUsageChart } from '@shared/ui/organisms/reports/WeeklyOfficeUsageChart';
import { useTranslation } from "react-i18next";
import { selectIconPack } from "@shared/store/auth.duck";
import { FreeBusyStatus } from '@features/whereabouts/services/WhereaboutOptions';
import { iconPackPath } from "@shared/utils/WhereaboutsHelper";
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

interface Props {
  hideUsageChart?: boolean;
  style?: any;
}

export function OfficeListSidebar(props: Props) {
  const dispatch = useDispatch();
  const offices = useSelector(selectActiveOffices);
  const activeDay = useSelector(selectActiveDay);
  const activeOffice = useSelector(selectedRootOffice);
  const allOffices = useSelector(selectAllOfficeEntities);
  const iconPack = useSelector(selectIconPack);
  const {t} = useTranslation();

  const officesForDropdown = useMemo(() => offices.map((office: OfficeEntity) => ({...office, name: office.label})), [offices]);

  const onSelect = (officeId: any) => {
    const office = findOfficeInOffices(allOffices, parseInt(officeId));
    const officeChildren = getOfficeChildren(parseInt(officeId), allOffices);

    if (officeChildren.length > 0) {
      dispatch(selectOffice(officeChildren[0]));
      dispatch(clearSelectedArea());
    } else if (office) {
      dispatch(selectOffice(office));
      dispatch(clearSelectedArea());
    } else {
      console.warn('No office found for', officeId);
    }
  }

  if (offices.length > 0) {
    return (
      <Wrapper data-test="office-sidebar" style={props?.style ?? {}}>
        <BodyRegular weight={600} style={{marginBottom: 8}}>{t('choose-office-to-view')}</BodyRegular>
        <OfficeSelectDropDown selectedId={activeOffice?.id}
                              onSelect={(office: any) => onSelect(office)}
                              items={officesForDropdown}
                              placeholder="textinput.choose-an-office" />
        {(!isMobile() && !props.hideUsageChart) && <>
          <WeeklyOfficeUsageChart office={activeOffice} style={{marginTop: 16}} date={activeDay} />
        </>}
      </Wrapper>
    )
  } else {
    return (
      <Wrapper data-test="office-sidebar">
        <Column>
          <OfficeImage style={{marginBottom: 24}} src={iconPackPath(iconPack, 'Office.svg', FreeBusyStatus.FREE)} alt="Ofifce" aria-hidden={false} />
          <HeadlineSmall style={{marginBottom: 8}}>{t('create-an-office')}</HeadlineSmall>
          <BodyRegular style={{marginBottom: 32}}>{t('create-an-office-description')}</BodyRegular>
          <OutlineButton size={ButtonSize.Large}
                         fullWidth={true}
                         click={() => window.location.href = '/manage/offices'}
                         text="button.create-office" />
        </Column>
      </Wrapper>
    )
  }
}

const Wrapper = styled.div`
  margin-top: 24px;
  button {
    max-width: 100%;
  }
  @media (${tablet}) {
    margin-top: 0;
  }
`

const OfficeImage = styled.img`
  align-self: center;
  width: 96px;
`

const OfficeSelectDropDown = styled(GenericDropDownList)<any>`
  width: 100%;
  background: white;
`
